import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { verifyStravaState } from '@/lib/strava-state';
import { LOGIN_WITH_STRAVA } from '@/graphql/mutations';

export function StravaCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const { t } = useTranslation('auth');
    const [loginWithStrava] = useMutation(LOGIN_WITH_STRAVA);
    const calledRef = useRef(false);

    useEffect(() => {
        if (calledRef.current) return;
        calledRef.current = true;

        const error = searchParams.get('error');
        if (error) {
            navigate('/login', { state: { stravaError: t('strava.error.denied') }, replace: true });
            return;
        }

        // CSRF protection: verify state parameter
        const state = searchParams.get('state');
        if (!verifyStravaState(state)) {
            navigate('/login', { state: { stravaError: t('strava.error.invalidState') }, replace: true });
            return;
        }

        const code = searchParams.get('code');
        if (!code) {
            navigate('/login', { replace: true });
            return;
        }

        loginWithStrava({ variables: { code } })
            .then(async ({ data }) => {
                if (data?.loginWithStrava?.token) {
                    await login(data.loginWithStrava.token);
                    navigate('/', { replace: true });
                }
            })
            .catch(() => {
                navigate('/login', { state: { stravaError: t('strava.error.failed') }, replace: true });
            });
    }, [searchParams, loginWithStrava, login, navigate, t]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4">
            <Loader2 className="animate-spin text-emerald-600" size={40} />
            <p className="text-slate-500 font-medium">{t('strava.connecting')}</p>
        </div>
    );
}
